/**
 * WmsLaunchAdmin — Super Admin page for the WMS developer: the "Open NicheSocial" button,
 * the JWT handoff contract, a mint-test-link tool, and the new-order alert scan.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, ExternalLink, Check, Link2, ShieldCheck, ShieldAlert, RefreshCw, Loader2, BellRing } from 'lucide-react';
import { useAuth } from './AuthContext';
import './WmsLaunchAdmin.css';

const API = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://nichesocial.app';

const phpSnippet = (origin) => `// routes/web.php
Route::get('/nichesocial/launch', [NicheSocialController::class, 'launch'])
    ->middleware('auth')->name('nichesocial.launch');

// app/Http/Controllers/NicheSocialController.php   (composer require firebase/php-jwt)
use Firebase\\JWT\\JWT;
use Illuminate\\Support\\Str;

class NicheSocialController extends Controller
{
    public function launch(Request $request)
    {
        $user = $request->user();
        $to   = $request->query('to', 'script'); // script | listings | valuation-tools | property-description | video
        $payload = [
            'iss' => 'nichecom-wms',
            'aud' => 'nichesocial',
            'iat' => time(),
            'exp' => time() + 300,                 // ≤ 15 min; 5 min is plenty for a redirect
            'jti' => (string) Str::uuid(),         // single use — NicheSocial rejects replays
            'email'         => $user->email,
            'wms_user_id'   => $user->id,
            'wms_branch_id' => $user->branch_id,
            'name'          => $user->name,
            'module'        => $to,
        ];
        $token = JWT::encode($payload, config('services.nichesocial.sso_secret'), 'HS256');
        return redirect("${origin}/sso?token={$token}&to={$to}");
    }
}

// config/services.php
'nichesocial' => ['sso_secret' => env('NICHESOCIAL_SSO_SECRET')],   // shared secret from NicheCom — never ship it to the browser`;

const htmlSnippet = `<!-- Anywhere in the WMS UI, e.g. the order page or the top nav -->
<a href="{{ route('nichesocial.launch', ['to' => 'script']) }}"
   class="btn btn-nichesocial" target="_blank" rel="noopener">
  Open NicheSocial
</a>`;

const useCopy = () => {
  const [copied, setCopied] = useState('');
  const copy = (key, text) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(key); toast.success('Copied'); setTimeout(() => setCopied(''), 1600); }).catch(() => toast.error('Copy failed'));
  };
  return [copied, copy];
};

const Snippet = ({ id, title, code, copied, onCopy }) => (
  <div className="wla-snippet" data-testid={`wla-snippet-${id}`}>
    <div className="wla-snippet-head">
      <span>{title}</span>
      <button type="button" className="wla-copy" onClick={() => onCopy(id, code)} data-testid={`wla-copy-${id}`}>
        {copied === id ? <Check size={13} /> : <Copy size={13} />} {copied === id ? 'Copied' : 'Copy'}
      </button>
    </div>
    <pre><code>{code}</code></pre>
  </div>
);

function MintTestLink({ modules }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [module, setModule] = useState('script');
  const [branchId, setBranchId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, copy] = useCopy();

  const mint = (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    const body = { email: email.trim(), module };
    if (branchId.trim()) body.wms_branch_id = Number(branchId);
    axios.post(`${API}/api/auth/sso/wms/mint-test-token`, body)
      .then((r) => setResult({ ok: true, url: `${ORIGIN}${r.data.path}`, expires: r.data.expires_in }))
      .catch((err) => setResult({ ok: false, error: err.response?.data?.detail || 'Could not mint a test link' }))
      .finally(() => setBusy(false));
  };

  return (
    <form className="wla-mint" onSubmit={mint} data-testid="wla-mint-form">
      <label>Email on NicheSocial<input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="agent@agency.co.uk" data-testid="wla-mint-email" /></label>
      <label>Land in
        <select value={module} onChange={(e) => setModule(e.target.value)} data-testid="wla-mint-module">
          {(modules || ['script']).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </label>
      <label>WMS branch id <small>(optional — auto-provisions a new agent)</small><input value={branchId} onChange={(e) => setBranchId(e.target.value)} inputMode="numeric" placeholder="10912" data-testid="wla-mint-branch" /></label>
      <button type="submit" className="wla-primary" disabled={busy || !email.trim()} data-testid="wla-mint-submit">
        {busy ? <Loader2 size={14} className="spin" /> : <Link2 size={14} />} Mint test link
      </button>
      {result && !result.ok && <p className="wla-error" data-testid="wla-mint-error">{result.error}</p>}
      {result && result.ok && (
        <div className="wla-mint-result" data-testid="wla-mint-result">
          <code data-testid="wla-mint-url">{result.url}</code>
          <div className="wla-mint-actions">
            <button type="button" className="wla-copy" onClick={() => copy('link', result.url)} data-testid="wla-mint-copy">{copied === 'link' ? <Check size={13} /> : <Copy size={13} />} Copy link</button>
            <a className="wla-copy is-solid" href={result.url} target="_blank" rel="noopener noreferrer" data-testid="wla-mint-open"><ExternalLink size={13} /> Open test link</a>
            <small>Single use · expires in {Math.round(result.expires / 60)} min · opens a fresh session for that user</small>
          </div>
        </div>
      )}
    </form>
  );
}

function AlertsPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/api/wms/alerts/status`).then((r) => setStatus(r.data)).catch(() => setStatus(false));
  useEffect(() => { load(); }, []);
  const run = () => {
    setBusy(true);
    axios.post(`${API}/api/wms/alerts/scan`)
      .then((r) => { toast.success(r.data.skipped ? 'A scan is already running' : `Scan done — ${r.data.new} new, ${r.data.resolved} resolved across ${r.data.users} people`); load(); })
      .catch(() => toast.error('Scan failed'))
      .finally(() => setBusy(false));
  };
  const last = status?.last_run;
  return (
    <div className="wla-alerts" data-testid="wla-alerts">
      <div className="wla-alerts-stats">
        <div><strong data-testid="wla-alerts-open">{status ? status.open_alerts : '–'}</strong><span>open nudges</span></div>
        <div><strong>{last ? `${last.new}/${last.resolved}` : '–'}</strong><span>new / resolved last run</span></div>
        <div><strong>{last ? `${last.users}` : '–'}</strong><span>people scanned{last?.errors ? ` · ${last.errors} errors` : ''}</span></div>
        <div><strong>{last ? new Date(last.finished_at || last.started_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'never'}</strong><span>last run{last ? ` · ${last.trigger}` : ''}</span></div>
      </div>
      <p className="wla-muted">
        {status?.cron_configured ? <><ShieldCheck size={13} /> Scheduled {status.schedule} — agents and branch managers get a bell + login nudge for every Social Pro order still needing a script.</> : <><ShieldAlert size={13} /> Cron secret missing — scheduled scans are off until <code>WEBHOOK_CRON_SECRET</code> is set.</>}
      </p>
      <button type="button" className="wla-primary" onClick={run} disabled={busy} data-testid="wla-alerts-run">
        {busy ? <Loader2 size={14} className="spin" /> : <BellRing size={14} />} Run scan now
      </button>
    </div>
  );
}

function SecretReveal() {
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, copy] = useCopy();
  const reveal = () => {
    setBusy(true);
    axios.get(`${API}/api/auth/sso/wms/secret`).then((r) => setSecret(r.data.secret)).catch((e) => toast.error(e.response?.data?.detail || 'Could not load the secret')).finally(() => setBusy(false));
  };
  return (
    <div className="wla-secret" data-testid="wla-secret">
      <p className="wla-muted"><ShieldAlert size={13} /> Give IT this shared secret <strong>out-of-band</strong> (password manager, not email). It goes in WMS as <code>NICHESOCIAL_SSO_SECRET</code> and must never reach the browser.</p>
      {secret ? (
        <div className="wla-secret-row">
          <code data-testid="wla-secret-value">{secret}</code>
          <button type="button" className="wla-copy" onClick={() => copy('secret', secret)} data-testid="wla-secret-copy">{copied === 'secret' ? <Check size={13} /> : <Copy size={13} />} Copy</button>
          <button type="button" className="wla-copy" onClick={() => setSecret(null)} data-testid="wla-secret-hide">Hide</button>
        </div>
      ) : (
        <button type="button" className="wla-copy" onClick={reveal} disabled={busy} data-testid="wla-secret-reveal">{busy ? <Loader2 size={13} className="spin" /> : <ShieldCheck size={13} />} Reveal shared secret</button>
      )}
    </div>
  );
}

export default function WmsLaunchAdmin() {
  const [spec, setSpec] = useState(null);
  const [copied, copy] = useCopy();
  useEffect(() => { axios.get(`${API}/api/auth/sso/wms/spec`).then((r) => setSpec(r.data)).catch(() => setSpec(false)); }, []);

  return (
    <div className="wla" data-testid="wms-launch-admin">
      <header className="wla-head">
        <div>
          <span className="wla-eyebrow">WMS → NicheSocial</span>
          <h2>WMS Launch Button</h2>
          <p>Everything the WMS developer needs for a one-click “Open NicheSocial” that skips our login page. Agents arriving this way see their branch’s Social Pro orders; agents who sign in with their WMS password see their own.</p>
        </div>
        <div className={`wla-status ${spec?.configured ? 'ok' : 'warn'}`} data-testid="wla-secret-status">
          {spec?.configured ? <ShieldCheck size={16} /> : <ShieldAlert size={16} />}
          {spec === null ? 'Checking…' : spec?.configured ? 'Shared secret configured' : 'Shared secret not set'}
        </div>
      </header>

      <section className="wla-section">
        <h3>1 · The button</h3>
        <p className="wla-muted">Drop this where agents work — the order page, the dashboard, the top nav. It hits a tiny WMS route that signs a token and redirects.</p>
        <div className="wla-snippets">
          <Snippet id="html" title="Blade / HTML" code={htmlSnippet} copied={copied} onCopy={copy} />
          <Snippet id="php" title="Laravel route + controller (firebase/php-jwt)" code={phpSnippet(ORIGIN)} copied={copied} onCopy={copy} />
        </div>
      </section>

      <section className="wla-section">
        <h3>2 · The handoff contract</h3>
        <div className="wla-rules">
          <span><strong>Landing URL</strong><code>{ORIGIN}/sso?token=&lt;jwt&gt;&amp;to=&lt;module&gt;</code></span>
          <span><strong>Signature</strong>{spec?.algorithm || 'HS256'} with the shared secret (we hold it as <code>WMS_SSO_SECRET</code>; share it out-of-band)</span>
          <span><strong>Lifetime</strong>≤ {spec ? Math.round(spec.max_lifetime_seconds / 60) : 15} min · single use (<code>jti</code>) — replays are refused</span>
          <span><strong>Modules</strong>{(spec?.modules || ['script']).join(' · ')}</span>
        </div>
        <table className="wla-claims" data-testid="wla-claims">
          <thead><tr><th>claim</th><th>value</th></tr></thead>
          <tbody>
            {Object.entries(spec?.claims || {}).map(([k, v]) => <tr key={k}><td><code>{k}</code></td><td>{String(v)}</td></tr>)}
          </tbody>
        </table>
        <p className="wla-muted">What we do: verify signature / iss / aud / exp / jti → match <code>wms_user_id</code>, then email → auto-provision a new agent when <code>wms_branch_id</code> maps to an enabled branch → issue a NicheSocial session and land on the module.</p>
        <SecretReveal />
      </section>

      <section className="wla-section">
        <h3>3 · Prove it works</h3>
        <p className="wla-muted">Mint a real signed link for any NicheSocial user and open it — exactly what WMS will do.</p>
        <MintTestLink modules={spec?.modules} />
      </section>

      <section className="wla-section">
        <h3>4 · New-order alerts</h3>
        <AlertsPanel />
      </section>
    </div>
  );
}
